// AI Service for WaterTAP UI
// This service integrates with OpenAI API to provide intelligent recommendations

const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';
const DEFAULT_MODEL = 'gpt-4o'; // Using GPT-4o-mini for cost efficiency

// Available flowsheets in WaterTAP UI
const AVAILABLE_FLOWSHEETS = [
    'NF-DSPM-DE flowsheet', 'BSM2 flowsheet', 'BSM2_P_extension flowsheet',
    'Dye Desalination flowsheet', 'Mechanical vapor compression flowsheet',
    'RO with energy recovery flowsheet', 'OARO flowsheet',
    'Granular Activated Carbon (GAC) flowsheet',
    'Electrodialysis with concentrate recirculation flowsheet',
    'LSRRO flowsheet', 'Generic treatment train flowsheet',
    'Microfiltration flowsheet', 'Pre-filtration flowsheet', 'UV Filtration flowsheet'
];

// Flowsheet URLs for direct navigation
const FLOWSHEET_URLS = {
    'Microfiltration flowsheet': '#/flowsheet/microfiltration_ui/config',
    'Pre-filtration flowsheet': '#/flowsheet/prefiltration_fixed_ui/config',
    'UV Filtration flowsheet': '#/flowsheet/UV_filtration_ui/config',
    'RO with energy recovery flowsheet': '#/flowsheet/RO_with_energy_recovery_flowsheet/config',
    'OARO flowsheet': '#/flowsheet/OARO_flowsheet/config',
    'LSRRO flowsheet': '#/flowsheet/LSRRO_flowsheet/config',
    'Granular Activated Carbon (GAC) flowsheet': '#/flowsheet/GAC_flowsheet/config',
    'Electrodialysis with concentrate recirculation flowsheet': '#/flowsheet/Electrodialysis_flowsheet/config',
    'Mechanical vapor compression flowsheet': '#/flowsheet/MVC_flowsheet/config',
    'BSM2 flowsheet': '#/flowsheet/BSM2_flowsheet/config',
    'BSM2_P_extension flowsheet': '#/flowsheet/BSM2_P_extension_flowsheet/config',
    'NF-DSPM-DE flowsheet': '#/flowsheet/NF_DSPM_DE_flowsheet/config',
    'Dye Desalination flowsheet': '#/flowsheet/Dye_Desalination_flowsheet/config',
    'Generic treatment train flowsheet': '#/flowsheet/Generic_treatment_train_flowsheet/config'
};

// Parameter knowledge base for each flowsheet
const FLOWSHEET_PARAMETERS = {
    'RO with energy recovery flowsheet': {
        'Watermassflowrate': { optimal: 0.75, unit: 'kg/s', min: 0.6, max: 0.9 },
        'Pressure': { optimal: 65, unit: 'bar', min: 50, max: 80 },
        'Temperature': { optimal: 25, unit: '°C', min: 20, max: 35 },
        'Recovery': { optimal: 0.4, unit: 'fraction', min: 0.3, max: 0.5 }
    },
    'OARO flowsheet': {
        'Watermassflowrate': { optimal: 5.5, unit: 'kg/s', min: 5, max: 6 },
        'Osmotic_pressure': { optimal: 30, unit: 'bar', min: 20, max: 40 },
        'Membrane_area': { optimal: 150, unit: 'm²', min: 100, max: 200 }
    },
    'Granular Activated Carbon (GAC) flowsheet': {
        'Apparentdensity': { optimal: 750, unit: 'kg/m³', min: 700, max: 800 },
        'Bed_depth': { optimal: 2.5, unit: 'm', min: 1.5, max: 3.0 },
        'Empty_bed_contact_time': { optimal: 10, unit: 'min', min: 5, max: 15 },
        'Carbon_usage_rate': { optimal: 0.2, unit: 'kg/m³', min: 0.1, max: 0.3 }
    },
    'Mechanical vapor compression flowsheet': {
        'Feedmassflow': { optimal: 40, unit: 'kg/s', min: 35, max: 45 },
        'Compression_ratio': { optimal: 2.0, unit: 'ratio', min: 1.5, max: 2.5 },
        'Evaporator_temperature': { optimal: 70, unit: '°C', min: 60, max: 80 },
        'Steam_economy': { optimal: 0.9, unit: 'fraction', min: 0.8, max: 0.95 }
    },
    'BSM2 flowsheet': {
        'Feedvolumetricflowrate': { optimal: 20500, unit: 'm³/d', min: 20000, max: 21000 },
        'Sludge_age': { optimal: 15, unit: 'days', min: 10, max: 20 },
        'MLSS': { optimal: 4000, unit: 'mg/L', min: 3000, max: 5000 },
        'Dissolved_oxygen': { optimal: 2.0, unit: 'mg/L', min: 1.5, max: 3.0 }
    },
    'Microfiltration flowsheet': {
        'Trans_membrane_pressure': { optimal: 0.5, unit: 'bar', min: 0.1, max: 2.0 },
        'Membrane_flux': { optimal: 50, unit: 'LMH', min: 20, max: 200 },
        'Membrane_area': { optimal: 100, unit: 'm²', min: 10, max: 1000 },
        'Particle_removal': { optimal: 0.99, unit: 'fraction', min: 0.90, max: 0.999 },
        'Fouling_factor': { optimal: 0.8, unit: 'fraction', min: 0.5, max: 1.0 },
        'Backwash_frequency': { optimal: 8, unit: 'hours', min: 1, max: 24 }
    },
    'Pre-filtration flowsheet': {
        'Water_recovery': { optimal: 0.95, unit: 'fraction', min: 0.90, max: 0.98 },
        'Removal_efficiency': { optimal: 0.95, unit: 'fraction', min: 0.90, max: 0.98 },
        'Operating_pressure': { optimal: 2.0, unit: 'bar', min: 1.5, max: 2.5 },
        'Antiscalant_dose': { optimal: 0.001, unit: 'kg/kg', min: 0.0005, max: 0.002 },
        'Acid_dose': { optimal: 0.0005, unit: 'kg/kg', min: 0.0002, max: 0.001 }
    },
    'UV Filtration flowsheet': {
        'UV_dose': { optimal: 40, unit: 'mJ/cm²', min: 10, max: 200 },
        'UV_lamp_power': { optimal: 5, unit: 'kW', min: 1, max: 50 },
        'UV_transmittance': { optimal: 0.85, unit: 'fraction', min: 0.70, max: 0.95 },
        'Log_reduction': { optimal: 4, unit: 'logs', min: 2, max: 6 },
        'Specific_energy': { optimal: 0.02, unit: 'kWh/m³', min: 0.005, max: 0.1 },
        'Water_recovery': { optimal: 0.99, unit: 'fraction', min: 0.95, max: 1.0 }
    }
};

// System prompt for OpenAI
const SYSTEM_PROMPT = `You are an expert AI assistant for Aquaflowz, a comprehensive water treatment modeling platform. You specialize in:

1. **Water Treatment Technology Selection**: Helping users choose the right flowsheet for their specific needs
2. **Parameter Optimization**: Suggesting optimal parameters for different flowsheets based on real Aquaflowz models
3. **Flowsheet Navigation**: Helping users find and understand different treatment technologies

**Available Flowsheets**: ${AVAILABLE_FLOWSHEETS.join(', ')}

**Key Technologies**:
- **RO (Reverse Osmosis)**: High-pressure membrane process for salt removal - use "RO with energy recovery flowsheet" or "OARO flowsheet" or "LSRRO flowsheet"
- **FO (Forward Osmosis)**: Low-energy osmotic process using draw solution
- **GAC (Granular Activated Carbon)**: Adsorption-based organic contaminant removal
- **BSM2**: Biological treatment for municipal wastewater
- **MVC (Mechanical Vapor Compression)**: Thermal desalination with energy recovery
- **Electrodialysis**: Ion exchange membrane process for salt removal
- **Microfiltration**: Membrane-based particle removal (0.1-10 μm) - use "Microfiltration flowsheet"
- **Pre-filtration**: Multi-stage pre-treatment for suspended solids removal - use "Pre-filtration flowsheet"
- **UV Filtration**: Ultraviolet disinfection for pathogen inactivation - use "UV Filtration flowsheet"

**IMPORTANT RULES**:
1. If user asks about RO (Reverse Osmosis), ONLY recommend RO-related flowsheets: "RO with energy recovery flowsheet", "OARO flowsheet", or "LSRRO flowsheet"
2. If user asks about electrodialysis, ONLY recommend "Electrodialysis with concentrate recirculation flowsheet"
3. Do NOT mix up different technologies - be specific and accurate
4. Always explain WHY you chose specific flowsheets based on the user's question

Always provide:
1. **Specific flowsheet recommendations** with reasoning (flowsheet names are clickable links)
2. **Parameter suggestions** with optimal values and ranges from real Aquaflowz models
3. **Clear explanations** of why these recommendations were chosen
4. **Practical guidance** for implementation

**IMPORTANT**: When recommending flowsheets, always format them as clickable links using markdown format: [Flowsheet Name](URL)

Respond in JSON format with these fields:
{
    "recommendations": ["flowsheet1", "flowsheet2"],
    "parameters": {"flowsheet_name": {"param": {"optimal": value, "unit": "unit", "min": min, "max": max}}},
    "explanation": "Why these recommendations were chosen",
    "additionalInsights": "Additional technical insights"
}`;

class AIService {
    constructor() {
        this.apiKey = null;
        this.isAvailable = false;
        this.lastError = null;
        this.loadApiKey();
    }

    isServiceAvailable() {
        return this.isAvailable;
    }

    getApiKey() {
        return this.apiKey;
    }

    getLastError() {
        return this.lastError;
    }

    loadApiKey() {
        // Try to get API key from environment variable first
        const envApiKey = process.env.REACT_APP_OPENAI_API_KEY;
        
        if (envApiKey && this.validateApiKey(envApiKey)) {
            this.apiKey = envApiKey;
            this.isAvailable = true;
            this.lastError = null;
            console.log('AI Service: API key loaded from environment variable');
            return;
        }

        // Fall back to localStorage if no environment variable
        const storedKey = localStorage.getItem('openai_api_key');
        if (storedKey && this.validateApiKey(storedKey)) {
            this.apiKey = storedKey;
            this.isAvailable = true;
            this.lastError = null;
            console.log('AI Service: API key loaded from localStorage');
            return;
        }

        // No valid API key found
        this.isAvailable = false;
        this.lastError = 'No valid OpenAI API key found. Please configure your API key in the .env file or through the UI settings.';
        console.warn('AI Service: No valid API key found');
    }

    async generateAIResponse(userInput, chatHistory = []) {
        if (!this.isServiceAvailable()) {
            const errorMsg = this.getLastError() || 'OpenAI API key not configured. Please configure your API key first.';
            throw new Error(errorMsg);
        }

        try {
            // Add context to prevent repetitive responses
            const contextInfo = `Current time: ${new Date().toLocaleString()}. User query: "${userInput}". Previous messages: ${chatHistory.length}`;
            
            // Add some randomness to the system prompt to prevent repetitive responses
            const randomVariations = [
                "Remember to be creative and varied in your explanations.",
                "Provide unique insights for each response.",
                "Avoid repetitive language and structure.",
                "Make each response feel fresh and personalized."
            ];
            
            const randomVariation = randomVariations[Math.floor(Math.random() * randomVariations.length)];
            
            const messages = [
                { role: 'system', content: SYSTEM_PROMPT + `\n\n${contextInfo}\n\n${randomVariation}` },
                ...chatHistory.slice(-8).map(msg => ({
                    role: msg.type === 'user' ? 'user' : 'assistant',
                    content: msg.message
                })),
                { role: 'user', content: userInput }
            ];

            const response = await fetch(OPENAI_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.apiKey}`
                },
                body: JSON.stringify({
                    model: DEFAULT_MODEL,
                    messages: messages,
                    temperature: 0.9,
                    max_tokens: 1000,
                    response_format: { type: "json_object" }
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = errorData.error?.message || response.statusText;
                
                // Store the specific error for debugging
                this.lastError = `OpenAI API error (${response.status}): ${errorMessage}`;
                
                // Throw specific error based on status code
                if (response.status === 401) {
                    throw new Error(`Invalid API key. Please check your OpenAI API key in the .env file. Error: ${errorMessage}`);
                } else if (response.status === 429) {
                    throw new Error(`Rate limit exceeded. Please try again later. Error: ${errorMessage}`);
                } else if (response.status === 500) {
                    throw new Error(`OpenAI server error. Please try again later. Error: ${errorMessage}`);
                } else {
                    throw new Error(`OpenAI API error (${response.status}): ${errorMessage}`);
                }
            }

            const data = await response.json();
            const aiResponse = data.choices[0].message.content;

            try {
                const parsedResponse = JSON.parse(aiResponse);
                return this.formatAIResponse(parsedResponse, userInput);
            } catch (parseError) {
                console.warn('Failed to parse AI response as JSON, using fallback:', parseError);
                throw new Error(`Failed to parse AI response. The API returned invalid JSON. Please try again.`);
            }

        } catch (error) {
            console.error('AI service error:', error);
            // Re-throw the error instead of falling back to fake data
            throw error;
        }
    }

    formatAIResponse(parsedResponse, userInput) {
        const response = {
            type: 'ai',
            message: '',
            timestamp: new Date()
        };

        // Build comprehensive response message with some variety
        let message = '';
        
        // Add variety to the opening
        const openings = [
            `Based on your requirements, I recommend these flowsheets:\n\n`,
            `After analyzing your needs, here are my top recommendations:\n\n`,
            `For your specific case, I suggest these flowsheets:\n\n`,
            `Based on the Aquaflowz models available, here are my picks:\n\n`
        ];
        
        const randomOpening = openings[Math.floor(Math.random() * openings.length)];

        if (parsedResponse.recommendations && parsedResponse.recommendations.length > 0) {
            message += randomOpening;
            parsedResponse.recommendations.forEach((rec, index) => {
                const url = this.getFlowsheetUrl(rec);
                if (url) {
                    message += `${index + 1}. [${rec}](${url})\n`;
                } else {
                    message += `${index + 1}. ${rec}\n`;
                }
            });
            message += '\n';
        }

        if (parsedResponse.explanation) {
            message += `Why these recommendations?\n\n${parsedResponse.explanation}\n\n`;
        }

        if (parsedResponse.parameters) {
            message += `Parameter Suggestions:\n\n`;
            Object.entries(parsedResponse.parameters).forEach(([flowsheetName, params]) => {
                message += `${flowsheetName}:\n`;
                Object.entries(params).forEach(([paramName, values]) => {
                    message += `• ${paramName}: ${values.optimal} ${values.unit} (range: ${values.min}-${values.max})\n`;
                });
                message += '\n';
            });
        }

        if (parsedResponse.additionalInsights) {
            message += `Additional Insights:\n\n${parsedResponse.additionalInsights}\n\n`;
        }

        response.message = message.trim();
        response.recommendations = parsedResponse.recommendations || [];
        response.parameters = parsedResponse.parameters || {};
        response.explanation = parsedResponse.explanation || '';
        response.additionalInsights = parsedResponse.additionalInsights || '';

        return response;
    }

    generateFallbackResponse(userInput) {
        const query = userInput.toLowerCase();
        const response = {
            type: 'ai',
            message: '',
            timestamp: new Date()
        };

        // Add variety to the opening
        const openings = [
            'Based on your query, here are my recommendations:\n\n',
            'After analyzing your needs, here are my suggestions:\n\n',
            'For your specific requirements, I recommend:\n\n',
            'Based on the available Aquaflowz flowsheets, here are my picks:\n\n'
        ];
        
        const randomOpening = openings[Math.floor(Math.random() * openings.length)];
        let message = randomOpening;

        // Use the improved recommendation method
        const recommendations = this.getAccurateRecommendations(userInput);
        
        if (recommendations.length > 0) {
            message += `Recommended Flowsheets:\n\n`;
            recommendations.forEach((rec, index) => {
                const url = this.getFlowsheetUrl(rec);
                if (url) {
                    message += `${index + 1}. [${rec}](${url})\n`;
                } else {
                    message += `${index + 1}. ${rec}\n`;
                }
            });
            message += '\n';
            
            // Add technology explanation
            if (query.includes('ro') || query.includes('reverse osmosis')) {
                message += `These RO flowsheets are perfect for salt removal and desalination:\n`;
                message += `• RO with energy recovery - Most efficient for seawater\n`;
                message += `• OARO - Good for brackish water\n`;
                message += `• LSRRO - Low salinity applications\n\n`;
            } else if (query.includes('electrodialysis')) {
                message += `Electrodialysis uses ion exchange membranes for salt removal:\n`;
                message += `• Lower energy requirements than RO\n`;
                message += `• Good for brackish water treatment\n\n`;
            }
        }

        message += `Next Steps:\n\n`;
        message += `1. Choose a flowsheet from the recommendations above\n`;
        message += `2. Click on any flowsheet to navigate to it directly\n`;
        message += `3. Use the suggested parameters in your flowsheet configuration\n\n`;
        
        // Add variety to the closing
        const closings = [
            `Would you like me to provide more specific parameter recommendations for any of these flowsheets?`,
            `Need help with specific parameters for any of these flowsheets? Just ask!`,
            `I can provide detailed parameter guidance for any flowsheet you're interested in.`,
            `Let me know if you'd like parameter details for any specific flowsheet!`
        ];
        
        message += closings[Math.floor(Math.random() * closings.length)];

        response.message = message;
        response.recommendations = recommendations;
        return response;
    }

    // NEW: Improved keyword matching for more accurate recommendations
    getAccurateRecommendations(userInput) {
        const input = userInput.toLowerCase();
        const recommendations = [];
        
        // RO-specific recommendations
        if (input.includes('ro') || input.includes('reverse osmosis')) {
            recommendations.push(
                'RO with energy recovery flowsheet',
                'OARO flowsheet', 
                'LSRRO flowsheet'
            );
        }
        
        // Electrodialysis-specific recommendations
        if (input.includes('electrodialysis') || input.includes('ion exchange')) {
            recommendations.push('Electrodialysis with concentrate recirculation flowsheet');
        }
        
        // GAC-specific recommendations
        if (input.includes('gac') || input.includes('activated carbon') || input.includes('organic')) {
            recommendations.push('Granular Activated Carbon (GAC) flowsheet');
        }
        
        // BSM2-specific recommendations
        if (input.includes('bsm2') || input.includes('biological') || input.includes('wastewater')) {
            recommendations.push('BSM2 flowsheet');
        }
        
        // MVC-specific recommendations
        if (input.includes('mvc') || input.includes('vapor compression') || input.includes('thermal')) {
            recommendations.push('Mechanical vapor compression flowsheet');
        }
        
        // Microfiltration-specific recommendations
        if (input.includes('microfiltration') || input.includes('microfiltration') || input.includes('particle removal') || input.includes('turbidity')) {
            recommendations.push('Microfiltration flowsheet');
        }
        
        // Pre-filtration-specific recommendations
        if (input.includes('pre-filtration') || input.includes('prefiltration') || input.includes('pre-treatment') || input.includes('pretreatment') || input.includes('suspended solids')) {
            recommendations.push('Pre-filtration flowsheet');
        }
        
        // UV Filtration-specific recommendations
        if (input.includes('uv') || input.includes('ultraviolet') || input.includes('disinfection') || input.includes('pathogen') || input.includes('bacteria') || input.includes('virus')) {
            recommendations.push('UV Filtration flowsheet');
        }
        
        // If no specific technology mentioned, provide general recommendations
        if (recommendations.length === 0) {
            if (input.includes('salt') || input.includes('desalination')) {
                recommendations.push('RO with energy recovery flowsheet', 'OARO flowsheet');
            } else if (input.includes('contaminant') || input.includes('chemical')) {
                recommendations.push('Granular Activated Carbon (GAC) flowsheet');
            } else if (input.includes('particle') || input.includes('turbidity')) {
                recommendations.push('Microfiltration flowsheet', 'Pre-filtration flowsheet');
            } else if (input.includes('disinfection') || input.includes('pathogen')) {
                recommendations.push('UV Filtration flowsheet');
            } else {
                recommendations.push('RO with energy recovery flowsheet', 'OARO flowsheet', 'Granular Activated Carbon (GAC) flowsheet', 'Microfiltration flowsheet');
            }
        }
        
        return recommendations;
    }

    getParameterSuggestions(flowsheetName) {
        return FLOWSHEET_PARAMETERS[flowsheetName] || {};
    }

    getAvailableFlowsheets() {
        return AVAILABLE_FLOWSHEETS;
    }

    getFlowsheetUrl(flowsheetName) {
        return FLOWSHEET_URLS[flowsheetName] || null;
    }

    getAllFlowsheetUrls() {
        return FLOWSHEET_URLS;
    }

    validateApiKey(apiKey) {
        return apiKey && apiKey.startsWith('sk-') && apiKey.length > 20;
    }

}

const aiService = new AIService();
aiService.loadApiKey();

export default aiService;
